import React, { useRef } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import AddIcon from '@material-ui/icons/Add';
import CircularProgress from '@material-ui/core/CircularProgress';
import ImageModal from '../../../components/ImageModal';
import { uploadImage } from '../../../helper';
import { apiPut } from '../../../brainApi';
import dataProvider from '../../../dataProvider';

const useStyles = makeStyles((theme) => ({
  unsetButton: {
    margin: 0,
    padding: 0,
    border: 'none',
    overflow: 'visible',
    background: 'none',
  },
  thumbnailContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  thumbnail: {
    width: 100,
    height: 100,
    marginRight: 10,
    marginBottom: 10,
    borderRadius: '5%',
    objectFit: 'cover',
    cursor: 'zoom-in',
  },
  uploadButtonThumbnail: {
    cursor: 'pointer',
    outline: 'thin dashed gray',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pointer: {
    cursor: 'pointer',
  },
  removeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    minWidth: theme.spacing(2),
  },
  removeIcon: {
    color: theme.palette.error.main,
  },
}));

const S3_VEHICLE_URL = 'https://zippity.s3.amazonaws.com/vehicle';

const UnifiedVehiclePhotos = ({
  vehicle,
  enlargedImage,
  setEnlargedImage,
  isUploading,
  setIsUploading,
  filenames,
  setVehicleRecord,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const translate = useTranslate();
  const title = translate('resources.vehicle.fields.photo_filenames');
  const inputFile = useRef(null);

  const handleInputChange = (e) => {
    const uploadedFileNames = [];
    setIsUploading(true);
    Promise.all(
      Array.from(e.target.files).map((file) => {
        return uploadImage('vehicle', file).then((filename) => {
          if (!filename) {
            notify(`There was a problem uploading ${filename}`, 'warning');
          }
          uploadedFileNames.push(filename);
        });
      }),
    ).then(() => {
      setIsUploading(false);
      apiPut(`/vehicle/${vehicle.id}`, {
        photo_filenames: [...vehicle.photo_filenames, ...uploadedFileNames],
      })
        .then(() => {
          dataProvider
            .getOne('vehicle', {
              id: vehicle.id,
            })
            .then(({ data }) => {
              setVehicleRecord(data);
            });
        })
        .catch((error) => {
          console.error(error);
          notify('There was a problem uploading your photos', 'warning');
        });
    });
  };

  const handleDelete = () => {
    const photoToDelete = filenames.find(
      (photoName) => photoName === enlargedImage,
    );
    const photosToKeep = filenames.filter((photo) => photo !== photoToDelete);
    apiPut(`/vehicle/${vehicle.id}`, {
      photo_filenames: photosToKeep,
    })
      .then(() => {
        setEnlargedImage(null);
        dataProvider
          .getOne('vehicle', {
            id: vehicle.id,
          })
          .then(({ data }) => {
            setVehicleRecord(data);
          });
      })
      .catch((error) => {
        console.error(error);
        notify('There was a problem deleting your photo', 'warning');
      });
  };

  return (
    <>
      <Grid container spacing={0}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
      </Grid>
      <Grid container spacing={0}>
        <Box p={1} className={classes.thumbnailContainer}>
          <button
            type="button"
            className={`${classes.unsetButton} ${classes.thumbnail} ${classes.uploadButtonThumbnail}`}
            onClick={() => inputFile.current.click()}
            aria-label="Upload a vehicle photo"
            disabled={isUploading}
          >
            {isUploading ? <CircularProgress size={24} /> : <AddIcon />}
          </button>
          <form>
            <input
              type="file"
              id="file"
              accept="image/*"
              multiple
              ref={inputFile}
              style={{ display: 'none' }}
              onChange={handleInputChange}
            />
          </form>
          {filenames.map((filename) => {
            const url = `${S3_VEHICLE_URL}/${filename}`;
            return (
              <button
                type="button"
                className={classes.unsetButton}
                onClick={() => setEnlargedImage(filename)}
              >
                <img src={url} className={classes.thumbnail} alt={title} />
              </button>
            );
          })}
        </Box>
        <ImageModal
          imageSource={
            enlargedImage ? `${S3_VEHICLE_URL}/${enlargedImage}` : null
          }
          setImageSource={setEnlargedImage}
          handleDelete={handleDelete}
        />
      </Grid>
    </>
  );
};

export default UnifiedVehiclePhotos;
